import { COLUMN_CLASS, KNOWN_STYLE_OPTIONS } from "../constants";
import type { ElementNode, InlineStyle, LabelStyled, ListNode, PairNode, ParsedNode, StyleOptions, StyleValue, Styled, TableNode } from "../types";

interface Resolved {
  className?: string;
  style?: InlineStyle;
}

const merge = (...values: (StyleValue | undefined)[]): Resolved => {
  const classes: string[] = [];
  let style: InlineStyle | undefined;
  values.forEach((value) => {
    if (!value) return;
    const { className, style: inline } = typeof value === "string" ? { className: value, style: undefined } : value;
    if (className) classes.push(className);
    if (inline) style = { ...style, ...inline };
  });
  return { className: classes.join(" ") || undefined, style };
};

const assign = (target: Styled, { className, style }: Resolved) => {
  if (className) target.className = className;
  if (style) target.style = style;
};

const assignLabel = (target: LabelStyled, { className, style }: Resolved) => {
  if (className) target.labelClassName = className;
  if (style) target.labelStyle = style;
};

const stylePair = (pair: PairNode, o: StyleOptions) => {
  assign(pair, merge(o.pairStyles));
  assignLabel(pair, merge(o.pairLabelStyles));
  const { className, style } = merge(o.pairValueStyles);
  if (className) pair.valueClassName = className;
  if (style) pair.valueStyle = style;
};

const styleTable = (table: TableNode, o: StyleOptions) => {
  assign(table, merge(o.tableStyles));
  table.rows.forEach((row) => {
    assign(row, merge(o.rowStyles));
    row.cells.forEach((cell) => {
      assign(cell, merge(o.cellStyles, cell.type === "th" ? o.thStyles : o.tdStyles));
      if (cell.pair) stylePair(cell.pair, o);
    });
  });
};

const styleList = (list: ListNode, o: StyleOptions) => {
  assign(list, merge(o.listStyles, list.type === "ol" ? o.olStyles : o.ulStyles));
  if (list.label) assignLabel(list, merge(o.listLabelStyles));
  if (list.pair) stylePair(list.pair, o);
  list.children.forEach((li) => {
    assign(li, merge(o.liStyles));
    if (li.label) assignLabel(li, merge(o.liLabelStyles));
    if (li.pair) stylePair(li.pair, o);
    li.children.forEach((sub) => styleList(sub, o));
  });
};

const isColumn = (node: ElementNode) => node.type === "div" && node.className === COLUMN_CLASS;

const styleNode = (node: ParsedNode, o: StyleOptions, inColumn: boolean) => {
  if (node.type === "text" || node.type === "raw") return;
  if (node.type === "pair") return stylePair(node as PairNode, o);
  if (node.type === "table") return styleTable(node as TableNode, o);
  if (node.type === "ul" || node.type === "ol") return styleList(node as ListNode, o);

  const el = node as ElementNode;
  const column = isColumn(el);
  if (column) {
    assign(el, merge(COLUMN_CLASS, o.columnStyles));
  } else if (inColumn && el.type === "div" && !el.className && !el.attrs) {
    assign(el, merge(o.pieceStyles));
  } else {
    assign(el, merge(el.className, o[`${el.type}Styles` as keyof StyleOptions]));
  }
  el.children.forEach((child) => styleNode(child, o, column));
};

const applyStyles = (nodes: ParsedNode[], options: StyleOptions) => {
  const unknown = Object.keys(options).filter((key) => !KNOWN_STYLE_OPTIONS.includes(key));
  if (unknown.length) console.warn(`htmlParser: unknown style option(s) ${unknown.join(", ")}`);
  nodes.forEach((node) => styleNode(node, options, false));
  return nodes;
};

export { applyStyles };
